import { Component, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-pagination',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-xl shadow-md p-6">
      <!-- Page Info -->
      <div class="text-sm text-gray-600 flex-shrink-0">
        Showing <span class="font-semibold text-gray-900">{{ startItem() }}</span> to 
        <span class="font-semibold text-gray-900">{{ endItem() }}</span> of 
        <span class="font-semibold text-gray-900">{{ totalItems() }}</span> {{ itemName() }}
      </div>

      <!-- Page Numbers Container with Scroll -->
      <div class="flex items-center gap-2 overflow-x-auto max-w-full pb-2 sm:pb-0 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
        <!-- Previous Button -->
        <button
          (click)="handlePrevious()"
          [disabled]="currentPage() === 1"
          aria-label="Go to previous page"
          class="px-3 py-2 rounded-lg border-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          [class.border-gray-300]="currentPage() === 1"
          [class.text-gray-400]="currentPage() === 1"
          [class.border-immovea-navy]="currentPage() !== 1"
          [class.text-immovea-navy]="currentPage() !== 1"
          [class.hover:bg-blue-50]="currentPage() !== 1">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
          </svg>
        </button>

        <!-- Page Numbers -->
        <ng-container *ngFor="let page of pageNumbers()">
          <!-- Ellipsis -->
          <span *ngIf="page === -1" class="px-2 text-gray-400">...</span>
          
          <!-- Page Button -->
          <button
            *ngIf="page !== -1"
            (click)="handlePageClick(page)"
            [attr.aria-label]="'Go to page ' + page"
            [attr.aria-current]="currentPage() === page ? 'page' : null"
            class="min-w-[40px] px-3 py-2 rounded-lg border-2 font-semibold transition-all"
            [class.bg-gradient-to-r]="currentPage() === page"
            [class.from-immovea-navy]="currentPage() === page"
            [class.to-blue-700]="currentPage() === page"
            [class.text-white]="currentPage() === page"
            [class.border-immovea-navy]="currentPage() === page"
            [class.shadow-lg]="currentPage() === page"
            [class.border-gray-300]="currentPage() !== page"
            [class.text-gray-700]="currentPage() !== page"
            [class.hover:bg-gray-50]="currentPage() !== page">
            {{ page }}
          </button>
        </ng-container>

        <!-- Next Button -->
        <button
          (click)="handleNext()"
          [disabled]="currentPage() === totalPages()"
          aria-label="Go to next page"
          class="px-3 py-2 rounded-lg border-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          [class.border-gray-300]="currentPage() === totalPages()"
          [class.text-gray-400]="currentPage() === totalPages()"
          [class.border-immovea-navy]="currentPage() !== totalPages()"
          [class.text-immovea-navy]="currentPage() !== totalPages()"
          [class.hover:bg-blue-50]="currentPage() !== totalPages()">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
          </svg>
        </button>
      </div>
    </div>
  `
})
export class PaginationComponent {
  currentPage = input.required<number>();
  totalItems = input.required<number>();
  itemsPerPage = input.required<number>();
  itemName = input<string>('items');
  
  pageChange = output<number>();
  
  totalPages() {
    return Math.ceil(this.totalItems() / this.itemsPerPage());
  }
  
  startItem() {
    return (this.currentPage() - 1) * this.itemsPerPage() + 1;
  }
  
  endItem() {
    return Math.min(this.currentPage() * this.itemsPerPage(), this.totalItems());
  }
  
  pageNumbers(): (number | -1)[] {
    const total = this.totalPages();
    const current = this.currentPage();
    const pages: (number | -1)[] = [];
    
    if (total <= 7) {
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }
    } else {
      if (current <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push(-1);
        pages.push(total);
      } else if (current >= total - 2) {
        pages.push(1);
        pages.push(-1);
        for (let i = total - 3; i <= total; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push(-1);
        for (let i = current - 1; i <= current + 1; i++) pages.push(i);
        pages.push(-1);
        pages.push(total);
      }
    }
    
    return pages;
  }
  
  handlePrevious() {
    if (this.currentPage() > 1) {
      this.pageChange.emit(this.currentPage() - 1);
    }
  }
  
  handleNext() {
    if (this.currentPage() < this.totalPages()) {
      this.pageChange.emit(this.currentPage() + 1);
    }
  }
  
  handlePageClick(page: number | -1) {
    if (typeof page === 'number' && page >= 1 && page <= this.totalPages()) {
      this.pageChange.emit(page);
    }
  }
}
